import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator,
} from "@/components/ui/input-otp";
import { Mail, User, Loader2, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopperRegisterPage = () => {
  const [step, setStep] = useState("profile");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) setEmail(emailParam);
  }, [searchParams]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/shoppers/register/request-otp`, { name, email });
      if (response.data.success) {
        toast.success("Verification code sent.");
        setStep("otp");
        setCountdown(300);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error("Enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/shoppers/register/verify`, { email, otp_code: otp });
      login(response.data.token, response.data.email, response.data.user_type);
      toast.success("Your shopper account is ready.");
      navigate("/shopper-profile");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="page-fade-in">
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-16 md:py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Shopper Signup</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Save Your Local Favorites</h1>
          <p className="text-gray-600 max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Create a shopper profile to follow businesses and markets across 614 Shop Local.
          </p>
        </div>
      </section>

      <section className="py-14 px-6">
        <div className="max-w-md mx-auto bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-[#42D78A]/10 flex items-center justify-center mb-5">
            <Heart className="w-6 h-6 text-[#42D78A]" />
          </div>
          {step === "profile" ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-[#1a1a1a] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-12 pl-12 rounded-lg border-gray-200" data-testid="shopper-name-input" />
                </div>
              </div>
              <div>
                <Label className="text-[#1a1a1a] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 pl-12 rounded-lg border-gray-200" data-testid="shopper-email-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="shopper-signup-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Verification Code"}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <button onClick={() => { setStep("profile"); setOtp(""); }} className="text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors" data-testid="shopper-back-btn">
                Back to profile details
              </button>
              <div>
                <h2 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Verify your email</h2>
                <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Enter the 6-digit code sent to {email}.</p>
              </div>
              <div className="flex justify-center otp-input-container">
                <InputOTP maxLength={6} value={otp} onChange={(v) => setOtp(v)} data-testid="shopper-otp-input" disabled={loading}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl border-gray-200" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl border-gray-200" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl border-gray-200" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl border-gray-200" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl border-gray-200" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl border-gray-200" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {countdown > 0 && <p className="text-center text-gray-500 text-sm">Code expires in <span className="font-medium text-[#1a1a1a]">{formatTime(countdown)}</span></p>}
              <Button onClick={handleVerify} disabled={loading || otp.length !== 6} className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="shopper-verify-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify & Create Profile"}
              </Button>
            </div>
          )}
          <p className="text-center text-sm text-gray-500 mt-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Already have an account? <Link to="/login" className="font-medium text-[#1a1a1a] hover:text-[#42D78A]">Log in</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ShopperRegisterPage;
